import { motion } from "framer-motion";
import { Instagram, Quote } from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";
import { HOSTS, SEGMENTS } from "@/data/content";

export default function HostsSection({ hosts }) {
  const host = hosts?.[0] || HOSTS[0];
  return (
    <section id="hosts" data-testid="hosts-section" className="scroll-mt-24 py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
        >
          <motion.p variants={fadeUp} className="mb-4 text-[11px] font-bold uppercase tracking-[0.24em] text-cherry">
            Your Host
          </motion.p>
          <motion.h2
            variants={fadeUp}
            className="mb-14 max-w-xl font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl"
          >
            The girl doing the{" "}
            <span className="font-garamond italic text-cherry">decoding.</span>
          </motion.h2>

          <motion.article
            variants={fadeUp}
            data-testid="host-card-morgan"
            className="group grid grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-12"
          >
            <div className="relative overflow-hidden rounded-3xl lg:col-span-5">
              <img
                src={host.image_url}
                alt={`${host.name}, host of City Girls Decoded™`}
                className="aspect-[4/5] w-full object-cover grayscale transition-all duration-700 group-hover:scale-[1.03] group-hover:grayscale-0"
              />
              <div className="absolute inset-0 bg-cherry/40 mix-blend-multiply transition-opacity duration-700 group-hover:opacity-0" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 flex items-end justify-between p-7">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-cherry">
                    {host.role}
                  </p>
                  <h3 className="mt-1.5 font-display text-2xl font-extrabold sm:text-3xl">
                    {host.name}
                  </h3>
                </div>
                <a
                  data-testid="host-social-morgan"
                  href={host.instagram}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${host.name} on Instagram`}
                  className="flex h-10 w-10 items-center justify-center rounded-full border border-white/30 text-white transition-colors hover:border-cherry hover:bg-cherry"
                >
                  <Instagram className="h-4 w-4" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-7">
              <p className="text-sm leading-relaxed text-white/65 sm:text-base lg:text-lg">
                {host.bio}
              </p>
              <div className="mt-6 flex items-start gap-3 border-l-2 border-cherry pl-4">
                <Quote className="mt-0.5 h-4 w-4 shrink-0 text-cherry" />
                <p className="font-garamond text-lg italic text-white/80 sm:text-xl lg:text-2xl">
                  {host.quote}
                </p>
              </div>
              <p className="mt-8 text-[11px] font-bold uppercase tracking-[0.24em] text-cherry">
                Raw. Unfiltered. Necessary.
              </p>
              <div className="relative mt-8 overflow-hidden rounded-3xl">
                <img
                  src={host.image_url_2 || host.image_url}
                  alt={`${host.name}, host of City Girls Decoded™, on a rooftop overlooking downtown LA at night — a friend's hand on her shoulder`}
                  className="aspect-[16/9] w-full object-cover grayscale transition-all duration-700 group-hover:scale-[1.02] group-hover:grayscale-0"
                />
                <div className="absolute inset-0 bg-cherry/40 mix-blend-multiply transition-opacity duration-700 group-hover:opacity-0" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent" />
              </div>
            </div>
          </motion.article>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
            {SEGMENTS.map((s) => (
              <motion.div
                key={s.id}
                variants={fadeUp}
                data-testid={`segment-card-${s.id}`}
                className="h-full rounded-3xl border border-white/10 bg-panel p-7 transition-colors duration-300 hover:border-cherry/50"
              >
                <p className="font-display text-lg font-bold leading-snug sm:text-xl">{s.title}</p>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{s.copy}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
